/**
 * MIDIExporter - Export patterns to Standard MIDI File (SMF) format
 *
 * Creates Type 1 MIDI files with multiple tracks:
 * - Track 0: Tempo and time signature
 * - Track 1-n: Instrument patterns
 */

export class MIDIExporter {
    constructor() {
        // MIDI note numbers for drum sounds (GM standard)
        this.drumMap = {
            kick: 36,      // C1 - Bass Drum 1
            snare: 38,     // D1 - Snare Drum 1
            clap: 39,      // D#1 - Hand Clap
            rimshot: 37,   // C#1 - Side Stick
            hihat: 42,     // F#1 - Closed Hi-Hat
            openhh: 46,    // A#1 - Open Hi-Hat
            lowtom: 45,    // A1 - Low Tom
            midtom: 47,    // B1 - Mid Tom 1
            hitom: 50,     // D2 - High Tom 1
            cowbell: 56,   // G#2 - Cowbell
            // 909
            kick909: 36,
            snare909: 38,
            clap909: 39,
            rimshot909: 37,
            hh909: 42,
            openhh909: 46,
            ride909: 51,   // D#2 - Ride Cymbal 1
            crash909: 49   // C#2 - Crash Cymbal 1
        };

        this.ppq = 480; // Pulses per quarter note (standard resolution)
    }

    /**
     * Export current pattern to a MIDI file (1 bar)
     * @param {Object} store - The application store
     * @param {number} bpm - Tempo in BPM
     * @returns {Blob} MIDI file as a Blob
     */
    export(store, bpm) {
        const patterns = store.patterns;
        const tracks = [];

        // Track 0: Tempo track (1 bar)
        tracks.push(this._createTempoTrack(bpm, 1));

        // TB-303 #1 (Channel 1)
        if (this._hasNotes(patterns.bass1)) {
            tracks.push(this._createMelodicTrack('TB-303 #1', patterns.bass1, patterns.bass1Notes, 0));
        }

        // TB-303 #2 (Channel 2)
        if (this._hasNotes(patterns.bass2)) {
            tracks.push(this._createMelodicTrack('TB-303 #2', patterns.bass2, patterns.bass2Notes, 1));
        }

        // Minimoog (Channel 3)
        if (patterns.minimoogGates && this._hasNotes(patterns.minimoogGates)) {
            tracks.push(this._createMelodicTrack('Minimoog', patterns.minimoogGates, patterns.minimoogNotes, 2));
        }

        // MS-20 (Channel 4)
        if (patterns.ms20Gates && this._hasNotes(patterns.ms20Gates)) {
            tracks.push(this._createMelodicTrack('MS-20', patterns.ms20Gates, patterns.ms20Notes, 3));
        }

        // Jupiter-8 (Channel 5)
        if (patterns.jupiter8Gates && this._hasNotes(patterns.jupiter8Gates)) {
            tracks.push(this._createMelodicTrack('Jupiter-8', patterns.jupiter8Gates, patterns.jupiter8Notes, 4));
        }

        // ARP 2600 (Channel 6)
        if (patterns.arp2600Gates && this._hasNotes(patterns.arp2600Gates)) {
            tracks.push(this._createMelodicTrack('ARP 2600', patterns.arp2600Gates, patterns.arp2600Notes, 5));
        }

        // Juno-106 (Channel 7)
        if (patterns.juno106Gates && this._hasNotes(patterns.juno106Gates)) {
            tracks.push(this._createMelodicTrack('Juno-106', patterns.juno106Gates, patterns.juno106Notes, 6));
        }

        // SH-101 (Channel 8)
        if (patterns.sh101Gates && this._hasNotes(patterns.sh101Gates)) {
            tracks.push(this._createMelodicTrack('SH-101', patterns.sh101Gates, patterns.sh101Notes, 7));
        }

        // TR-808 (Channel 10 - drums)
        const drum808Patterns = this._collectDrumPatterns(patterns, ['kick', 'snare', 'clap', 'rimshot', 'hihat', 'openhh', 'lowtom', 'midtom', 'hitom', 'cowbell']);
        if (drum808Patterns.length > 0) {
            tracks.push(this._createDrumTrack('TR-808', drum808Patterns, 9)); // Channel 10 (0-indexed = 9)
        }

        // TR-909 (Channel 10 - drums, same channel)
        const drum909Patterns = this._collectDrumPatterns(patterns, ['kick909', 'snare909', 'clap909', 'rimshot909', 'hh909', 'openhh909', 'ride909', 'crash909']);
        if (drum909Patterns.length > 0) {
            tracks.push(this._createDrumTrack('TR-909', drum909Patterns, 9));
        }

        // Build the MIDI file
        const midiData = this._buildMIDIFile(tracks);
        return new Blob([midiData], { type: 'audio/midi' });
    }

    /**
     * Export full song arrangement to a MIDI file
     * Reuses the single-pattern logic, chaining patterns one after another
     * @param {Object} store - The application store
     * @param {number} bpm - Tempo in BPM
     * @returns {Blob} MIDI file as a Blob
     */
    exportSong(store, bpm) {
        const song = store.getSong();
        const patternBanks = store.patternBanks;

        // Calculate total bars
        const totalBars = song.reduce((sum, step) => sum + step.bars, 0);

        const tracks = [];

        // Track 0: Tempo track with full song length
        tracks.push(this._createTempoTrack(bpm, totalBars));

        // Build expanded pattern list: each entry is one bar of a pattern
        // e.g., [{pattern: 'A'}, {pattern: 'A'}, {pattern: 'B'}, {pattern: 'B'}] for A x2, B x2
        const expandedBars = [];
        for (const step of song) {
            for (let i = 0; i < step.bars; i++) {
                expandedBars.push(patternBanks[step.pattern]);
            }
        }

        // TB-303 #1 (Channel 1)
        tracks.push(this._createSongTrackFromBars('TB-303 #1', expandedBars, 'bass1', 'bass1Notes', 0));

        // TB-303 #2 (Channel 2)
        tracks.push(this._createSongTrackFromBars('TB-303 #2', expandedBars, 'bass2', 'bass2Notes', 1));

        // Minimoog (Channel 3)
        tracks.push(this._createSongTrackFromBars('Minimoog', expandedBars, 'minimoogGates', 'minimoogNotes', 2));

        // MS-20 (Channel 4)
        tracks.push(this._createSongTrackFromBars('MS-20', expandedBars, 'ms20Gates', 'ms20Notes', 3));

        // Jupiter-8 (Channel 5)
        tracks.push(this._createSongTrackFromBars('Jupiter-8', expandedBars, 'jupiter8Gates', 'jupiter8Notes', 4));

        // ARP 2600 (Channel 6)
        tracks.push(this._createSongTrackFromBars('ARP 2600', expandedBars, 'arp2600Gates', 'arp2600Notes', 5));

        // Juno-106 (Channel 7)
        tracks.push(this._createSongTrackFromBars('Juno-106', expandedBars, 'juno106Gates', 'juno106Notes', 6));

        // SH-101 (Channel 8)
        tracks.push(this._createSongTrackFromBars('SH-101', expandedBars, 'sh101Gates', 'sh101Notes', 7));

        // TR-808 (Channel 10 - drums)
        const drum808Keys = ['kick', 'snare', 'clap', 'rimshot', 'hihat', 'openhh', 'lowtom', 'midtom', 'hitom', 'cowbell'];
        tracks.push(this._createSongDrumTrackFromBars('TR-808', expandedBars, drum808Keys, 9));

        // TR-909 (Channel 10 - drums)
        const drum909Keys = ['kick909', 'snare909', 'clap909', 'rimshot909', 'hh909', 'openhh909', 'ride909', 'crash909'];
        tracks.push(this._createSongDrumTrackFromBars('TR-909', expandedBars, drum909Keys, 9));

        // Filter out empty tracks (no events except name and end)
        const nonEmptyTracks = tracks.filter((track, index) => {
            // Always keep tempo track (index 0)
            if (index === 0) return true;
            // Keep if track has more than 2 events (name + end of track)
            return track.events.length > 2;
        });

        // Build the MIDI file
        const midiData = this._buildMIDIFile(nonEmptyTracks);
        return new Blob([midiData], { type: 'audio/midi' });
    }

    /**
     * Create a melodic track from expanded bars (reuses single-pattern logic)
     */
    _createSongTrackFromBars(name, expandedBars, gateKey, notesKey, channel) {
        const events = [];
        const stepsPerQuarter = 4;
        const ticksPerStep = this.ppq / stepsPerQuarter;

        // Track name
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x03,
            data: this._stringToBytes(name)
        });

        let currentTick = 0;

        // Process each bar
        for (const patterns of expandedBars) {
            const gates = patterns[gateKey] || [];
            const notes = patterns[notesKey] || [];

            // 16 steps per bar (same as single pattern)
            for (let step = 0; step < 16; step++) {
                if (gates[step]) {
                    const noteName = notes[step] || 'C3';
                    const midiNote = this._noteNameToMidi(noteName);
                    const velocity = 100;

                    // Note On
                    events.push({
                        delta: currentTick,
                        type: 'noteOn',
                        channel,
                        note: midiNote,
                        velocity
                    });
                    currentTick = 0;

                    // Note Off (slightly shorter for legato)
                    events.push({
                        delta: ticksPerStep - 10,
                        type: 'noteOff',
                        channel,
                        note: midiNote,
                        velocity: 0
                    });
                    currentTick = 10;
                } else {
                    currentTick += ticksPerStep;
                }
            }
        }

        // End of track
        events.push({
            delta: currentTick,
            type: 'meta',
            subtype: 0x2F,
            data: []
        });

        return { name, events };
    }

    /**
     * Create a drum track from expanded bars
     */
    _createSongDrumTrackFromBars(name, expandedBars, drumKeys, channel) {
        const events = [];
        const stepsPerQuarter = 4;
        const ticksPerStep = this.ppq / stepsPerQuarter;
        const ticksPerBar = this.ppq * 4;

        // Track name
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x03,
            data: this._stringToBytes(name)
        });

        // Collect all events with absolute times
        const allEvents = [];
        let barOffset = 0;

        for (const patterns of expandedBars) {
            for (const drumKey of drumKeys) {
                const pattern = patterns[drumKey];
                const note = this.drumMap[drumKey];

                if (pattern) {
                    for (let step = 0; step < 16; step++) {
                        if (pattern[step]) {
                            const tick = barOffset + step * ticksPerStep;
                            allEvents.push({ tick, note, type: 'noteOn', velocity: 100 });
                            allEvents.push({ tick: tick + ticksPerStep - 10, note, type: 'noteOff', velocity: 0 });
                        }
                    }
                }
            }
            barOffset += ticksPerBar;
        }

        // Sort by time
        allEvents.sort((a, b) => a.tick - b.tick);

        // Convert to delta times
        let lastTick = 0;
        for (const evt of allEvents) {
            events.push({
                delta: evt.tick - lastTick,
                type: evt.type,
                channel,
                note: evt.note,
                velocity: evt.velocity
            });
            lastTick = evt.tick;
        }

        // End of track
        events.push({
            delta: barOffset - lastTick,
            type: 'meta',
            subtype: 0x2F,
            data: []
        });

        return { name, events };
    }

    /**
     * Check if pattern has any active notes
     */
    _hasNotes(pattern) {
        if (!pattern) return false;
        return pattern.some(step => step);
    }

    /**
     * Collect drum patterns from the store
     */
    _collectDrumPatterns(patterns, drumKeys) {
        const result = [];
        for (const key of drumKeys) {
            if (patterns[key] && this._hasNotes(patterns[key])) {
                result.push({ key, pattern: patterns[key], note: this.drumMap[key] });
            }
        }
        return result;
    }

    /**
     * Create tempo track (Track 0)
     * @param {number} bpm - Tempo in BPM
     * @param {number} totalBars - Total number of bars (default 1)
     */
    _createTempoTrack(bpm, totalBars = 1) {
        const events = [];

        // Time signature: 4/4
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x58, // Time Signature
            data: [4, 2, 24, 8] // 4/4, 24 MIDI clocks per metronome click, 8 32nd notes per quarter
        });

        // Tempo: microseconds per quarter note
        const microsecondsPerBeat = Math.round(60000000 / bpm);
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x51, // Set Tempo
            data: [
                (microsecondsPerBeat >> 16) & 0xFF,
                (microsecondsPerBeat >> 8) & 0xFF,
                microsecondsPerBeat & 0xFF
            ]
        });

        // End of track (at the end of totalBars bars = 4 quarter notes per bar)
        events.push({
            delta: this.ppq * 4 * totalBars,
            type: 'meta',
            subtype: 0x2F, // End of Track
            data: []
        });

        return { name: 'Tempo', events };
    }

    /**
     * Create a melodic instrument track
     */
    _createMelodicTrack(name, gates, notes, channel) {
        const events = [];
        const stepsPerQuarter = 4; // 16th notes = 4 per quarter
        const ticksPerStep = this.ppq / stepsPerQuarter;

        // Track name
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x03, // Track Name
            data: this._stringToBytes(name)
        });

        let currentTick = 0;

        for (let step = 0; step < 16; step++) {
            if (gates[step]) {
                const noteName = notes ? notes[step] : 'C3';
                const midiNote = this._noteNameToMidi(noteName);
                const velocity = 100;

                // Note On
                events.push({
                    delta: currentTick,
                    type: 'noteOn',
                    channel,
                    note: midiNote,
                    velocity
                });
                currentTick = 0;

                // Note Off (after 1 step duration, slightly shorter for legato)
                events.push({
                    delta: ticksPerStep - 10,
                    type: 'noteOff',
                    channel,
                    note: midiNote,
                    velocity: 0
                });
                currentTick = 10;
            } else {
                currentTick += ticksPerStep;
            }
        }

        // End of track
        events.push({
            delta: currentTick,
            type: 'meta',
            subtype: 0x2F,
            data: []
        });

        return { name, events };
    }

    /**
     * Create a drum track
     */
    _createDrumTrack(name, drumPatterns, channel) {
        const events = [];
        const stepsPerQuarter = 4;
        const ticksPerStep = this.ppq / stepsPerQuarter;

        // Track name
        events.push({
            delta: 0,
            type: 'meta',
            subtype: 0x03,
            data: this._stringToBytes(name)
        });

        // Collect all events with absolute times, then convert to deltas
        const allEvents = [];

        for (const { pattern, note } of drumPatterns) {
            for (let step = 0; step < 16; step++) {
                if (pattern[step]) {
                    const tick = step * ticksPerStep;
                    allEvents.push({ tick, note, type: 'noteOn', velocity: 100 });
                    allEvents.push({ tick: tick + ticksPerStep - 10, note, type: 'noteOff', velocity: 0 });
                }
            }
        }

        // Sort by time
        allEvents.sort((a, b) => a.tick - b.tick);

        // Convert to delta times
        let lastTick = 0;
        for (const evt of allEvents) {
            events.push({
                delta: evt.tick - lastTick,
                type: evt.type,
                channel,
                note: evt.note,
                velocity: evt.velocity
            });
            lastTick = evt.tick;
        }

        // End of track
        const endTick = 16 * ticksPerStep;
        events.push({
            delta: endTick - lastTick,
            type: 'meta',
            subtype: 0x2F,
            data: []
        });

        return { name, events };
    }

    /**
     * Convert note name (e.g., "C3", "F#4") to MIDI note number
     */
    _noteNameToMidi(noteName) {
        const noteMap = { 'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11 };
        const match = noteName.match(/^([A-G])(#|b)?(\d+)$/i);
        if (!match) return 60; // Default to C4

        let note = noteMap[match[1].toUpperCase()];
        if (match[2] === '#') note++;
        else if (match[2] === 'b') note--;

        const octave = parseInt(match[3]) + 1; // MIDI octave is 1 higher
        return octave * 12 + note;
    }

    /**
     * Convert string to byte array
     */
    _stringToBytes(str) {
        return Array.from(str).map(c => c.charCodeAt(0));
    }

    /**
     * Write variable-length quantity
     */
    _writeVarLen(value) {
        const bytes = [];
        bytes.push(value & 0x7F);
        value >>= 7;
        while (value > 0) {
            bytes.unshift((value & 0x7F) | 0x80);
            value >>= 7;
        }
        return bytes;
    }

    /**
     * Build the complete MIDI file
     */
    _buildMIDIFile(tracks) {
        const chunks = [];

        // Header chunk
        const headerData = [
            0x00, 0x01,                         // Format 1
            (tracks.length >> 8) & 0xFF,        // Number of tracks (high byte)
            tracks.length & 0xFF,               // Number of tracks (low byte)
            (this.ppq >> 8) & 0xFF,             // Time division (high byte)
            this.ppq & 0xFF                     // Time division (low byte)
        ];
        chunks.push(this._createChunk('MThd', headerData));

        // Track chunks
        for (const track of tracks) {
            const trackData = this._encodeTrackEvents(track.events);
            chunks.push(this._createChunk('MTrk', trackData));
        }

        // Combine all chunks
        const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.length;
        }

        return result;
    }

    /**
     * Create a MIDI chunk
     */
    _createChunk(type, data) {
        const chunk = new Uint8Array(8 + data.length);
        // Type (4 bytes)
        for (let i = 0; i < 4; i++) {
            chunk[i] = type.charCodeAt(i);
        }
        // Length (4 bytes, big-endian)
        chunk[4] = (data.length >> 24) & 0xFF;
        chunk[5] = (data.length >> 16) & 0xFF;
        chunk[6] = (data.length >> 8) & 0xFF;
        chunk[7] = data.length & 0xFF;
        // Data
        chunk.set(data, 8);
        return chunk;
    }

    /**
     * Encode track events to bytes
     */
    _encodeTrackEvents(events) {
        const bytes = [];

        for (const event of events) {
            // Delta time
            bytes.push(...this._writeVarLen(event.delta));

            if (event.type === 'meta') {
                bytes.push(0xFF, event.subtype);
                bytes.push(...this._writeVarLen(event.data.length));
                bytes.push(...event.data);
            } else if (event.type === 'noteOn') {
                bytes.push(0x90 | event.channel, event.note, event.velocity);
            } else if (event.type === 'noteOff') {
                bytes.push(0x80 | event.channel, event.note, event.velocity);
            }
        }

        return bytes;
    }

    /**
     * Trigger download of the MIDI file
     */
    download(blob, filename = 'pattern.mid') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}
